import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { collection, doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useCollection } from "../hooks/useCollection";
import { useDoc } from "../hooks/useDoc";
import { Spinner } from "./Spinner";
import { n } from "../lib/format";
import {
  distinctVariantText,
  productionSourceKind,
  productionSourceTitle,
  type ProductionMirrorFields,
} from "../lib/productionFields";

/**
 * Üretim listesinde satırın ALTINDA açılan kalem listesi.
 *
 * NEDEN: Üretim ekibi bir kaydın ne içerdiğini görmek için her seferinde detay
 * sayfasına gidip geri dönüyordu. Atölye tabletinde bu, kuyruğu baştan sona
 * gezmeyi pahalı hâle getiriyor. Panel sayfa DEĞİŞTİRMEDEN aynı bilgiyi verir;
 * detay sayfası kaldırılmadı, panelin içinde "Detayı aç" bağlantısı durur.
 *
 * VERİ KAYNAĞI — iki aşamalı, çünkü canlı veride `production.itemSummary`
 * BOŞ (400 kayıt tarandı, hiçbirinde yok):
 *   1) `itemSummary` doluysa doğrudan o kullanılır (ek okuma yapılmaz).
 *   2) Boşsa ve kayıt siparişten doğduysa `orders/{id}/lineItems` okunur —
 *      detay sayfasının okuduğu koleksiyonun aynısı.
 * Görsel: `lineItems` görsel taşımıyor; eksik görseller `products/{id}.imageUrl`
 * üzerinden TEK SEFERLİK `getDoc` ile tamamlanır (dinleyici açılmaz, ürün
 * kartı nadiren değişir). Görsel yoksa gri kutu + "görsel yok" yazısı.
 *
 * Panel yalnızca satır açıkken monte edilir; kapalı satır hiçbir okuma yapmaz.
 */

/* ── Rozetler ─────────────────────────────────────────────────────────────
 * Sipariş / SSH / Stok Siparişi rozeti hem liste satırında hem bu panelde
 * kullanılır. Tek yerde tanımlıdır ki iki ekranda renk/metin ayrışmasın.
 * ──────────────────────────────────────────────────────────────────────── */

export type ProductionSource = ProductionMirrorFields;

/**
 * Rozet metni; checkbox'ın erişilebilir etiketinde de kullanılır.
 *
 * ★ Etiket artık `lib/productionFields` üzerinden üretilir. Eskiden SSH ve stok
 * siparişi rozetleri `sourceLabel`'a HİÇ bakmadan doğrudan doküman kimliğini
 * basıyordu: sunucu "SSH #DMNTH1028TR" yazsa bile ekranda
 * "SSH-w16dATnoVeItlllmlvd3" görünüyordu.
 */
export function sourceBadgeText(p: ProductionSource): string {
  return productionSourceTitle(p);
}

function sourceBadgeClass(p: ProductionSource): string {
  if (p.sourceType === "ssh") return "bg-red-100 text-red-800";
  if (p.sourceType === "stockOrder") return "bg-blue-100 text-blue-800";
  return "bg-green-100 text-green-800";
}

/**
 * Kaynak rozeti.
 *
 * `compact` — rozet metni satır BAŞLIĞIYLA aynı olduğunda kullanılır. Müşteri
 * adı olmayan kayıtta başlık da `productionSourceTitle`a düşüyor ve ekranda
 * "Sipariş 77554" ile "Sipariş 77554" alt alta iki kez basılıyordu. Compact
 * hâlde rozet yalnızca TÜRÜ yazar ("Sipariş"); renk kodu ve bilgi korunur.
 */
export function SourceBadge({
  source,
  compact = false,
}: {
  source: ProductionSource;
  compact?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${sourceBadgeClass(source)}`}
    >
      {compact ? productionSourceKind(source) : sourceBadgeText(source)}
    </span>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────── */

/** `production.itemSummary` elemanı — sayfadaki tiple yapısal olarak aynıdır. */
interface SummaryItem {
  title?: string;
  productName?: string;
  variantTitle?: string;
  quantity?: number;
  imageUrl?: string;
  productId?: string;
}

/** `orders/{id}/lineItems` dokümanı — yalnızca panelin kullandığı alanlar. */
interface LineItemDoc {
  title?: string;
  productName?: string;
  variantTitle?: string;
  optionSize?: string;
  sku?: string;
  quantity?: number;
  imageUrl?: string;
  productId?: string;
  sequence?: number;
  isProductDeleted?: boolean;
}

/** Sipariş dokümanından SADECE not alanları okunur; PII panelde gösterilmez. */
interface OrderNoteDoc {
  productionNote?: string;
  customerNote?: string;
}

/** İki kaynağın da indirgendiği tek satır biçimi. */
interface PanelItem {
  key: string;
  name: string;
  /** Varyant / ölçü gibi ikincil ad parçası. */
  detail?: string;
  quantity?: number;
  imageUrl?: string;
  productId?: string;
  sku?: string;
  isProductDeleted?: boolean;
}

export interface ProductionItemsPanelProps {
  productionId: string;
  source: ProductionSource;
  /** `production.itemSummary` — doluysa ek okuma yapılmaz. */
  itemSummary?: SummaryItem[];
  /** `production.note` — üretim notu. */
  note?: string;
  /** Panel başlığında gösterilecek ek rozetler (statü, acil, stok düşüldü…). */
  badges?: ReactNode;
  /**
   * Kaynak sipariş `orders` koleksiyonunda YOK (göç kaybı). Liste ekranı bunu
   * varlık kontrolüyle tespit eder; panel "kalem yok" demek yerine SEBEBİ yazar.
   */
  sourceMissing?: boolean;
}

export function ProductionItemsPanel({
  productionId,
  source,
  itemSummary,
  note,
  badges,
  sourceMissing = false,
}: ProductionItemsPanelProps) {
  const summary = useMemo(() => itemSummary ?? [], [itemSummary]);
  const hasSummary = summary.length > 0;

  /** Yalnızca özet yoksa ve kayıt siparişten doğduysa kalemler okunur. */
  const orderId = source.sourceType === "order" ? (source.sourceId ?? "") : "";
  const lineItemsOrderId = hasSummary ? "" : orderId;

  const itemsQ = useCollection<LineItemDoc>(
    () => (lineItemsOrderId ? collection(db, "orders", lineItemsOrderId, "lineItems") : null),
    [lineItemsOrderId],
  );

  /** Not alanları sipariş dokümanında durur (`productionNote`, `customerNote`). */
  const orderQ = useDoc<OrderNoteDoc>(
    () => (orderId ? doc(db, "orders", orderId) : null),
    [orderId],
  );

  const items: PanelItem[] = useMemo(() => {
    /* ★ Varyant metni ürün adının İÇİNDE de geçiyorsa ikinci kez basılmaz.
       Canlıda `itemSummary` kalemlerinin 5'i böyle:
       "Imra Vestiyer – 120 x 38 - Keten Ekru" + variantTitle "Keten Ekru"
       ekranda "… Keten Ekru · Keten Ekru" oluyordu. */
    if (hasSummary) {
      return summary.map((it, i) => {
        const name = it.title ?? it.productName ?? "Ürün";
        return {
          key: `s-${i}`,
          name,
          detail: distinctVariantText(name, it.variantTitle),
          quantity: it.quantity,
          imageUrl: it.imageUrl,
          productId: it.productId,
        };
      });
    }
    return [...itemsQ.docs]
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((it) => {
        const name = it.title ?? it.productName ?? "Ürün";
        return {
          key: it.id,
          name,
          detail: distinctVariantText(name, it.variantTitle ?? it.optionSize),
          quantity: it.quantity,
          imageUrl: it.imageUrl,
          productId: it.productId,
          sku: it.sku,
          isProductDeleted: it.isProductDeleted,
        };
      });
  }, [hasSummary, summary, itemsQ.docs]);

  /**
   * Eksik görseller ürün kartından tamamlanır. Tek seferlik okuma; aynı ürün
   * ikinci kez istenmez (`fetchedRef`). Okunamayan ürün için boş dize yazılır,
   * böylece sonsuz yeniden deneme olmaz.
   */
  const [productImages, setProductImages] = useState<Record<string, string>>({});
  const fetchedRef = useRef<Set<string>>(new Set());

  const missingKey = useMemo(
    () =>
      [...new Set(items.filter((i) => !i.imageUrl && i.productId).map((i) => i.productId!))]
        .sort()
        .join(","),
    [items],
  );

  useEffect(() => {
    let cancelled = false;
    const ids = (missingKey ? missingKey.split(",") : []).filter(
      (id) => !fetchedRef.current.has(id),
    );
    if (ids.length === 0) return;
    ids.forEach((id) => fetchedRef.current.add(id));

    void Promise.all(
      ids.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "products", id));
          const url = snap.exists() ? (snap.data() as { imageUrl?: unknown }).imageUrl : undefined;
          return [id, typeof url === "string" ? url : ""] as const;
        } catch (e) {
          console.error("Ürün görseli okunamadı", id, e);
          return [id, ""] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setProductImages((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      cancelled = true;
    };
  }, [missingKey]);

  /**
   * İş tarifi ve özel istek metinleri — boşsa satır hiç basılmaz.
   *
   * ★ SSH ve stok siparişinde `note` bir "not" DEĞİL, ÜRETİLECEK İŞİN TARİFİDİR
   * ("… için ayak tablası üretilecektir · Ürün: X · Miktar: 1"). "Üretim notu"
   * başlığı atölyeye bunun opsiyonel bir açıklama olduğunu düşündürüyordu;
   * başlık artık kaynağa göre değişir.
   */
  const workLabel =
    source.sourceType === "ssh"
      ? "Üretilecek iş (SSH)"
      : source.sourceType === "stockOrder"
        ? "Üretilecek iş (stok siparişi)"
        : "Üretim notu";

  const notes = useMemo(
    () =>
      [
        { label: workLabel, text: note },
        { label: "Siparişin üretim notu", text: orderQ.data?.productionNote },
        { label: "Müşteri isteği", text: orderQ.data?.customerNote },
      ].filter((x): x is { label: string; text: string } => typeof x.text === "string" && x.text.trim().length > 0),
    [workLabel, note, orderQ.data?.productionNote, orderQ.data?.customerNote],
  );

  const loading = !hasSummary && lineItemsOrderId !== "" && itemsQ.loading;

  return (
    <div className="space-y-3 border-t border-gray-200 bg-gray-50 p-3">
      {/* Rozetler + detay bağlantısı */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SourceBadge source={source} />
          {badges}
        </div>
        <Link
          className="btn-secondary noprint"
          to={`/production/${encodeURIComponent(productionId)}`}
        >
          Detayı aç
        </Link>
      </div>

      {/* ★ Kaynağı kayıp kayıt: panel "kalem yok" demeden ÖNCE sebebi yazar. */}
      {sourceMissing ? (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
          <span className="font-semibold">Kaynak sipariş veritabanında yok (göç kaybı).</span> Bu
          kaydın müşterisi, kalemleri ve tutarı okunamıyor; kesim ve ayak listesinde de görünmez.
          İşi başlatmadan önce ofise bildirin.
        </p>
      ) : null}

      {/* Üretim notu / özel istek */}
      {notes.length > 0 ? (
        <ul role="list" className="space-y-2">
          {notes.map((x) => (
            <li key={x.label} className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="font-semibold">{x.label}:</span> {x.text}
            </li>
          ))}
        </ul>
      ) : source.sourceType === "ssh" ? (
        // Boş SSH kaydı SESSİZ geçilmez: atölye "not yok" ile "tarif taşınmamış"
        // arasındaki farkı görsün, kaydı ofise geri sorabilsin.
        <p className="text-sm text-amber-900">
          Bu SSH kaydının iş tarifi üretim kaydına taşınmamış. Kaydı SSH ekranından yeniden üretime
          gönderin ya da ofise bildirin.
        </p>
      ) : (
        <p className="text-sm text-gray-700">Üretim notu / özel istek girilmemiş.</p>
      )}

      {/* Kalemler */}
      {loading ? (
        <div className="flex items-center gap-2 py-2">
          <Spinner size="sm" label="Kalemler yükleniyor" />
          <span aria-hidden="true" className="text-sm text-gray-700">
            Kalemler yükleniyor…
          </span>
        </div>
      ) : itemsQ.error && !hasSummary ? (
        <p role="alert" className="text-sm text-red-800">
          {itemsQ.error}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-700">
          {source.sourceType === "order"
            ? sourceMissing
              ? "Kalem listesi yok, çünkü kaynak sipariş kaydı veritabanında bulunmuyor."
              : "Bu siparişte kalem kaydı yok."
            : source.sourceType === "ssh"
              ? "Bu SSH kaydında ürün özeti yok; üretilecek iş yukarıdaki tarifte yazılıdır."
              : "Bu kayıt siparişten doğmadı; kalem listesi tutulmuyor. Kesim ölçüleri detay ekranındadır."}
        </p>
      ) : (
        <ul role="list" className="space-y-2">
          {items.map((it) => {
            const img = it.imageUrl || (it.productId ? productImages[it.productId] : "") || "";
            const linkable = Boolean(it.productId) && it.isProductDeleted !== true;
            return (
              <li
                key={`${productionId}-${it.key}`}
                className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-3 ring-1 ring-gray-200"
              >
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100">
                  {img ? (
                    <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <span className="px-1 text-center text-xs text-gray-600">görsel yok</span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {linkable ? (
                      <Link className="link" to={`/products/${encodeURIComponent(it.productId!)}`}>
                        {it.name}
                      </Link>
                    ) : (
                      it.name
                    )}
                    {it.detail ? <span className="text-gray-700"> · {it.detail}</span> : null}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-600">
                    {it.sku ? `SKU ${it.sku}` : "SKU yok"}
                    {it.isProductDeleted ? " · üretimden kalkmış ürün" : ""}
                  </p>
                </div>

                <span className="text-sm font-semibold tabular-nums text-gray-900">
                  {n(it.quantity, " adet")}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ProductionItemsPanel;
